import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';

import t from 'react-translate';
import { AngularContext } from 'react-app';

import { DataDownloadFile, ReportStatus } from 'redux/schemas/api/custom-downloads';
import { getCurrentUser } from 'redux/selectors/users';
import { generateCustomDataReport, getCustomDataReport } from 'redux/actions/custom-downloads';
import { useAppDispatch } from 'redux/store';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import useCustomReportPusherEvents from 'custom_downloads/hooks/use-custom-report-pusher-events';
import useShowAllToggle from 'shared/hooks/use-show-all-toggle';
import DataDownloadsTable from 'custom_downloads/components/data-downloads-table';
import { config } from '@config/pendo.config.json';

type DataDownloadsListModalProps = {
  show: boolean;
  reportId: number | null;
  courseId?: number | null;
  reportStatus?: ReportStatus;
  isDailySftp?: boolean;
  onClose: () => void;
  title: string;
};

const DEFAULT_MIN = 4;

const DataDownloadsListModal = ({
  show,
  reportId,
  courseId,
  reportStatus,
  isDailySftp,
  onClose,
  title,
}: DataDownloadsListModalProps) => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { catalogId } = $state.params;

  const currentUser = useSelector(getCurrentUser);
  const currentUserName = currentUser?.fullName ?? '';

  const [downloads, setDownloads] = useState<DataDownloadFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { showAll, visibleItems: visibleFiles, hasMore, toggleShowAll, resetShowAll } = useShowAllToggle(downloads, DEFAULT_MIN);

  const getDownloadsList = useCallback((onceLoaded: boolean) => {
    if (!reportId) return;
    dispatch(getCustomDataReport({ catalogId, reportId }))
      .unwrap()
      .then((result) => {
        setDownloads(result.downloadDetails ?? []);
      })
      .catch(() => {
        if (!onceLoaded) setDownloads([]);
      })
      .finally(() => {
        setIsLoading(false);
        // Always resolve the in-flight "generating" state once a refresh completes,
        // even if the refetch itself failed, so a transient error can't strand the UI.
        if (onceLoaded) setIsGenerating(false);
      });
  }, [catalogId, dispatch, reportId]);

  useEffect(() => {
    if (!show || !reportId) {
      setDownloads([]);
      resetShowAll();
      setIsGenerating(false);
      return;
    }
    setIsLoading(true);
    setIsGenerating(reportStatus === ReportStatus.GENERATING);
    getDownloadsList(false);
  }, [show, reportId, reportStatus, resetShowAll, getDownloadsList]);

  useCustomReportPusherEvents(show ? courseId : null, (data) => {
    if (!reportId || data.courseId !== courseId || data.reportId !== reportId) return;
    if (data.reportType && data.reportType !== 'download') return;
    getDownloadsList(true);
  });

  const handleGenerate = async () => {
    if (!reportId) return;
    setIsGenerating(true);
    try {
      await dispatch(generateCustomDataReport({ catalogId, reportId })).unwrap();
    } catch {
      setIsGenerating(false);
    }
  };

  return (
    <NvModal
      type={ModalType.DYNAMIC}
      show={show}
      width={720}
      fullHeight={false}
      header={title}
      onClose={onClose}
      body={(
        <LoadingWrapper
          isLoaded={!isLoading}
          loaderType={LoaderType.PLACEHOLDER}
          className='w-100 p-5'
          keepRendered={false}
        >
          <>
            <DataDownloadsTable
              isGenerating={isGenerating}
              currentUserName={currentUserName}
              visibleFiles={visibleFiles}
              isDailySftp={isDailySftp}
            />

            {hasMore && (
              <div className='text-center mt-4'>
                <button
                  type='button'
                  className='btn btn-link text-primary'
                  onClick={toggleShowAll}
                  data-qa={config.pendo.customDownloads.listModal.showAll}
                >
                  {showAll ? t.SHARED.SHOW_LESS() : t.SHARED.SHOW_ALL()}
                </button>
              </div>
            )}

            <div className='d-flex justify-content-center mt-8'>
              <Button
                variant='primary'
                onClick={handleGenerate}
                disabled={!reportId || isGenerating}
                data-qa={config.pendo.customDownloads.listModal.generateLatest}
              >
                {isGenerating
                  ? t.CUSTOM_DOWNLOADS.GENERATING()
                  : t.CUSTOM_DOWNLOADS.GENERATE_LATEST_VERSION()}
              </Button>
            </div>
          </>
        </LoadingWrapper>
      )}
    />
  );
};

export default DataDownloadsListModal;
